import { Check } from 'lucide-react';
import { pinnedRoomId } from '../../authConfig';
import { useRoom } from '../../room';

/**
 * What this operator has told the app to do for them. Nothing here changes anything anybody else
 * sees — which is the one thing that is *not* true of the default-tags card above it in the same
 * pane, so this card keeps its own heading and its own "yours, not the room's" line rather than
 * relying on the pane's name to carry either.
 *
 * The preference lives in this browser (see `preferences.ts`), and the pane says so rather than
 * leaving it to be found out: an operator who sets this at the podium and finds it hasn't
 * followed them home would read that as a bug instead of as the documented trade-off.
 *
 * Every click is a save. There is no draft here, so nothing for the unsaved-work guard to hold
 * and no error path to surface — localStorage either takes it or the whole feature quietly
 * degrades to "first room", which is where it started.
 */

/**
 * Hoisted rather than declared inside the pane: a component defined in the render body is a new
 * type every render, so React would unmount and remount these inputs on each click and drop
 * focus mid-way through arrowing down the group.
 */
function RoomOption({
  title,
  detail,
  checked,
  onSelect,
}: {
  title: string;
  detail: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-3 border-b border-ink-800/5 p-3 last:border-0 hover:bg-felt-50/40">
      <input type="radio" name="default-room" checked={checked} onChange={onSelect} />
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-ink-950">{title}</span>
        <span className="block text-xs text-ink-800/60">{detail}</span>
      </span>
      {checked ? <Check className="size-4 shrink-0 text-felt-700" /> : null}
    </label>
  );
}

export function PreferencesPane() {
  const { rooms, roomId, defaultRoomId, setDefaultRoomId } = useRoom();

  const first = rooms[0];
  // A stored default naming a room this operator can no longer see is ignored when the app
  // resolves a room, so it must not look selected here either — the list would otherwise report a
  // choice that is not in force, with the row it names missing.
  const selected = rooms.some((r) => r.id === defaultRoomId) ? defaultRoomId : null;
  const opensOn = rooms.find((r) => r.id === (selected ?? first?.id));

  // Dev only, and empty in every checked-in .env — but where it is set it outranks this, so the
  // screen says so rather than showing a setting that silently does nothing.
  const pinned = pinnedRoomId ? rooms.find((r) => r.id === pinnedRoomId) : undefined;

  return (
    <section className="max-w-3xl rounded-xl border border-ink-800/10 bg-white p-6 shadow-sm">
      <h2 className="font-display font-semibold text-ink-950">Preferences</h2>
      <p className="mt-0.5 text-xs text-ink-800/60">
        Yours, not the room's. Saved in this browser, so they apply on this machine only and
        nobody else in the room sees them.
      </p>

      <div className="mt-5 border-t border-ink-800/10 pt-4">
        <div className="mb-1 text-[10px] uppercase tracking-wider text-ink-800/50">
          Room to open on
        </div>
        <p className="mb-3 text-xs text-ink-800/60">
          {rooms.length > 1
            ? 'Which of your rooms the app starts on. You can still switch at any time from the room list in the header.'
            : 'You operate one room, so the app always opens on it. This matters once you are assigned more than one.'}
        </p>

        <div className="rounded-lg border border-ink-800/10">
          <RoomOption
            title="No default"
            detail={
              first
                ? `Opens on whichever room comes first — currently ${first.name}.`
                : 'Opens on whichever room comes first.'
            }
            checked={selected === null}
            onSelect={() => setDefaultRoomId(null)}
          />
          {rooms.map((r) => (
            <RoomOption
              key={r.id}
              title={r.name}
              detail={r.id === roomId ? `${r.location} · the room you are in now` : r.location}
              checked={selected === r.id}
              onSelect={() => setDefaultRoomId(r.id)}
            />
          ))}
        </div>

        {pinned ? (
          <p className="mt-3 text-[10px] text-ink-800/40">
            This build pins room-web to {pinned.name} (VITE_ROOM_ID), which takes precedence — the
            choice above applies once that is cleared.
          </p>
        ) : opensOn ? (
          <p className="mt-3 text-[10px] text-ink-800/40">
            Next time you open Brush Room it will start on {opensOn.name}.
          </p>
        ) : null}
      </div>
    </section>
  );
}
